import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import logoUrl from "@/assets/waiterix-logo.png";
import { Link } from "wouter";

export default function Terms() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-3" data-testid="link-home">
            <img src={logoUrl} alt="Waiterix" className="h-10 w-auto" />
            <span className="text-xl font-bold">Waiterix</span>
          </Link>
          <Button onClick={() => window.location.href = "/"} variant="ghost" data-testid="button-back-home">
            Back to Home
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold mb-8">Waiterix Terms and Conditions</h1>
        <p className="text-muted-foreground mb-8">Last Updated: Feb 13, 2026</p>

        <Card className="p-8 mb-6">
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <h2 className="text-2xl font-bold mb-4">1. Acceptance of Terms</h2>
            <p className="mb-4">
              By accessing or using Waiterix ("Service"), you agree to be bound by these Terms and Conditions. If you do not agree to these Terms, you may not access or use the Service.
            </p>
            <p className="mb-4">
              These Terms apply to all hospitality businesses, restaurants, cafes, hotels, and other service establishments using the Waiterix platform.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">2. Description of Service</h2>
            <p className="mb-4">
              Waiterix is an AI-native hospitality operations platform that provides automated front-of-house services through a coordinated team of AI workers.
            </p>
            <p className="mb-4">The platform includes, but is not limited to, the following services:</p>
            <h3 className="text-xl font-semibold mb-2 mt-4 text-primary">AI Waiter</h3>
            <p className="mb-2">AI-powered ordering assistant capable of:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>taking customer orders</li>
              <li>answering menu questions</li>
              <li>recommending menu items</li>
              <li>supporting multiple languages</li>
              <li>assisting with QR-based ordering</li>
            </ul>
            <h3 className="text-xl font-semibold mb-2 mt-4 text-primary">AI Receptionist</h3>
            <p className="mb-2">AI-powered front desk assistant capable of:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>answering customer calls</li>
              <li>managing reservations</li>
              <li>handling customer inquiries</li>
              <li>assisting with bookings</li>
              <li>providing restaurant information 24/7</li>
            </ul>
            <h3 className="text-xl font-semibold mb-2 mt-4 text-primary">AI Business Manager</h3>
            <p className="mb-2">Operational AI designed to assist hospitality businesses with:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>operational analytics</li>
              <li>business insights</li>
              <li>menu optimization</li>
            </ul>
            <p className="mt-4 italic">Waiterix may continuously update or improve these services.</p>

            <h2 className="text-2xl font-bold mb-4 mt-8">3. User Accounts</h2>
            <p className="mb-4">To access the Service, users must create an account. You are responsible for:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>maintaining the confidentiality of account credentials</li>
              <li>all activity occurring under your account</li>
              <li>ensuring account information is accurate and current</li>
              <li>notifying Waiterix immediately of any unauthorized account access</li>
            </ul>
            <p className="mb-4">Waiterix is not responsible for losses resulting from unauthorized use of your account.</p>

            <h2 className="text-2xl font-bold mb-4 mt-8">4. Subscription & Pricing</h2>
            <p className="mb-4">Waiterix operates on a subscription model.</p>
            <h3 className="text-xl font-semibold mb-2 mt-4">Base (Starter) Subscription</h3>
            <p className="mb-4">Waiterix services start at <strong>$149 per month</strong>. This includes access to the Waiterix platform and core AI worker functionality.</p>
            <h3 className="text-xl font-semibold mb-2 mt-4">AI Usage Fees</h3>
            <p className="mb-4">Additional AI usage fees may apply depending on platform usage and AI processing requirements.</p>
            <h3 className="text-xl font-semibold mb-2 mt-4">Payment Terms</h3>
            <p className="mb-4">By subscribing to Waiterix:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>you agree to pay all subscription fees</li>
              <li>payments are billed monthly unless otherwise agreed</li>
              <li>subscriptions renew automatically unless cancelled</li>
            </ul>
            <h3 className="text-xl font-semibold mb-2 mt-4">Cancellation</h3>
            <p className="mb-4">You may cancel your subscription at any time through your account dashboard. Cancellation will take effect at the end of the current billing period.</p>

            <h2 className="text-2xl font-bold mb-4 mt-8">5. 30-Day Money Back Guarantee</h2>
            <p className="mb-4">Waiterix offers a 30-day satisfaction guarantee for the Starter tier subscription.</p>
            <p className="mb-2 font-semibold">If you are not satisfied within the first 30 days of subscription:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>you may request a full refund</li>
              <li>refund requests must be submitted within the 30-day window</li>
            </ul>
            <p className="mb-4 italic">After the guarantee period, no refunds are provided for unused service time or partial billing periods.</p>

            <h2 className="text-2xl font-bold mb-4 mt-8">6. AI Services and Limitations</h2>
            <p className="mb-4">Waiterix relies on artificial intelligence systems and third-party AI providers to deliver its services. By using Waiterix, you acknowledge that:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>AI responses may occasionally contain inaccuracies</li>
              <li>AI systems may misunderstand requests or generate imperfect responses</li>
              <li>hospitality staff should review AI interactions when necessary</li>
              <li>AI is designed to assist operations but should not replace reasonable human oversight.</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">7. AI Waiter Voice Interactions</h2>
            <p className="mb-4">When customers interact with the AI Waiter through voice or microphone access:</p>
            <h3 className="text-xl font-semibold mb-2 mt-4">Microphone Access</h3>
            <p className="mb-4">Customers may grant microphone access for voice interaction. Audio data may be transmitted to Waiterix servers and AI providers for real-time processing.</p>
            <h3 className="text-xl font-semibold mb-2 mt-4">Audio Storage</h3>
            <p className="mb-4">Audio is generally processed in real time and is not permanently stored unless required for service improvement or compliance purposes.</p>
            <h3 className="text-xl font-semibold mb-2 mt-4">Order Verification</h3>
            <p className="mb-4">Customers are responsible for reviewing and confirming their order before submission.</p>
            <h3 className="text-xl font-semibold mb-2 mt-4">Allergies and Dietary Restrictions</h3>
            <p className="mb-4 text-destructive font-semibold">While the AI Waiter may provide allergen information:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>customers must confirm critical dietary information with restaurant staff</li>
              <li>Waiterix is not liable for allergic reactions or dietary issues resulting from AI-generated responses.</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">8. Payment Processing</h2>
            <p className="mb-4">Waiterix integrates with third-party payment processors including:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Stripe (USA & Europe)</li>
              <li>Paystack (Africa)</li>
              <li>Adyen (Asia)</li>
              <li>other supported payment providers</li>
            </ul>
            <p className="mb-4">By using the Service, you agree to comply with the payment processor's terms. Waiterix is not responsible for payment processing errors or disputes, and you are responsible for any transaction fees charged by payment providers.</p>

            <h2 className="text-2xl font-bold mb-4 mt-8">9. User Content and Data</h2>
            <p className="mb-4">Users retain ownership of all content uploaded to Waiterix, including menus, restaurant branding, item descriptions, customer order data, and operational information.</p>
            <p className="mb-4">By using Waiterix, you grant the platform a license to store, process, display, and analyze such content for the purpose of providing and improving the Service.</p>

            <h2 className="text-2xl font-bold mb-4 mt-8">10. Future Platform Features</h2>
            <p className="mb-4">Waiterix may introduce additional features, including but not limited to:</p>
            <h3 className="text-xl font-semibold mb-2 mt-4">Super Menu Marketplace</h3>
            <p className="mb-4">Waiterix may list participating restaurants in a global digital menu marketplace where customers can discover and order from available restaurants. Businesses will be notified prior to listing and may opt out at any time.</p>
            <h3 className="text-xl font-semibold mb-2 mt-4">Customer Mobile Applications</h3>
            <p className="mb-4">Waiterix may release customer-facing applications allowing users to save favorite meals, reorder quickly, and interact with AI services. Businesses will have the option to opt out of participation.</p>

            <h2 className="text-2xl font-bold mb-4 mt-8">11. Prohibited Uses</h2>
            <p className="mb-4 underline font-semibold italic">You agree not to:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>use the Service for illegal purposes</li>
              <li>sales of alcoholic drinks</li>
              <li>violate applicable laws or regulations</li>
              <li>upload malicious code or compromise system security</li>
              <li>infringe on intellectual property rights</li>
              <li>reverse engineer or replicate the Service</li>
              <li>misuse AI features to harm customers or users.</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">12. Account Termination</h2>
            <p className="mb-4">You may delete your account at any time. Upon account deletion, access to the Service will be terminated, associated data may be removed, and subscriptions will be cancelled.</p>
            <p className="mb-4">Waiterix reserves the right to suspend or terminate accounts that violate these Terms.</p>

            <h2 className="text-2xl font-bold mb-4 mt-8">13. Limitation of Liability</h2>
            <p className="mb-4 italic">The Service is provided "as is" without warranties of any kind.</p>
            <p className="mb-2">Waiterix shall not be liable for:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>service interruptions</li>
              <li>AI inaccuracies</li>
              <li>third-party system failures</li>
              <li>payment processor issues</li>
              <li>data loss</li>
              <li>lost profits or business opportunities.</li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">14. Communications & Updates</h2>
            <p className="mb-4">
              I agree to be contacted by Waiterix via phone call, email, and SMS/text message regarding products, services, and updates. I understand I can opt out at any time through my account settings or by following the instructions in the messages received.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">15. Modifications to Terms</h2>
            <p className="mb-4">Waiterix may update these Terms from time to time. Users will be notified of significant changes through email, dashboard notifications, or website updates.</p>
            <p className="mb-4 font-semibold">Continued use of the Service constitutes acceptance of updated Terms.</p>

            <h2 className="text-2xl font-bold mb-4 mt-8">16. Contact Information</h2>
            <p className="mb-4">For questions regarding these Terms:</p>
            <p className="mb-4">
              Email: <a href="mailto:support@waiterix.com" className="text-primary hover:underline">support@waiterix.com</a><br />
              Website: <a href="https://waiterix.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://waiterix.com</a>
            </p>
          </div>
        </Card>

        <div className="text-center">
          <Button onClick={() => window.location.href = "/"} size="lg" data-testid="button-accept-terms">
            Back to Home
          </Button>
        </div>
      </main>

      <footer className="border-t py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© 2025 Waiterix. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
